export const MAX_PREVIEW_ROWS = 5;
export const UNSUPPORTED_DATE_ERR =
  'Unable to detect a supported date format.  Please contact support for assistance.';

export const EXPECTED_HSA_HEADERS = [
  'Month',
  'Device',
  'Customer ID',
  'Account name',
  'Campaign ID',
  'Ad group ID',
  'Keyword ID',
  'Search keyword',
  'Search keyword match type',
  'Display URL domain',
  'Search Impr. share (Auction Insights)',
  'Search overlap rate',
  'Position above rate',
  'Top of page rate',
  'Abs. Top of page rate',
  'Search outranking share',
] as const;
export type HSAColHeader = (typeof EXPECTED_HSA_HEADERS)[number];

// Validate columns that include ID values (i.e. Campaign ID, Ad Group ID, etc.)
const validateIDValue = (val: string) => {
  // Should only contain numbers (0-9) and possibly hyphens (-)
  return /^[0-9](-?[0-9])*$/.test(val);
};

// Detect the percentage format for columns which have percentage values
export const detectPercentageFormat = (value: string): string => {
  if (typeof value !== 'string') return '';

  const val = value.trim();

  if (/^-?\d+(\.\d+)?$/.test(val)) {
    return 'NUMERIC_DECIMAL'; // Ex: 0.383747573
  }

  if (/^-?\d+(\.\d+)?%$/.test(val)) {
    return 'PERCENT_DOT_DECIMAL'; // Ex: 38.37%
  }

  if (/^-?\d+(,\d+)?%$/.test(val)) {
    return 'PERCENT_COMMA_DECIMAL'; // Ex: 38,37%
  }

  return 'UNKNOWN_FORMAT';
};

export const getValidPercentageFormat = (percentFormat: Set<string>) => {
  // Check if there is only one percentage format and if so return it
  const validPercentageFormat = [
    'NUMERIC_DECIMAL',
    'PERCENT_DOT_DECIMAL',
    'PERCENT_COMMA_DECIMAL',
  ];
  const foundPercentageFormat = validPercentageFormat.filter((format) =>
    percentFormat.has(format)
  );

  if (foundPercentageFormat.length === 0) {
    throw new Error('Invalid percentage format');
  }
  if (foundPercentageFormat.length > 1) {
    throw new Error('Invalid: Multiple percentage format detected');
  }
  return foundPercentageFormat[0];
};

// ---- Begin date format validation ---
const validDatesNumeric: Record<string, RegExp> = {
  'MM-DD-YY': /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{2})$/,
  'MM/DD/YY': /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{2})$/,

  'DD-MM-YY': /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{2})$/,
  'DD/MM/YY': /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{2})$/,

  'YY-DD-MM': /^(\d{2})-(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])$/,
  'YY/DD/MM': /^(\d{2})\/(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])$/,

  'YY-MM-DD': /^(\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
  'YY/MM/DD': /^(\d{2})\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/,

  'DD-MM-YYYY':
    /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(20\d{2}|2[1-9]\d{2})$/,
  'DD/MM/YYYY':
    /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(20\d{2}|2[1-9]\d{2})$/,

  'MM-DD-YYYY':
    /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(20\d{2}|2[1-9]\d{2})$/,
  'MM/DD/YYYY':
    /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(20\d{2}|2[1-9]\d{2})$/,

  'YYYY-MM-DD':
    /^(20\d{2}|2[1-9]\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
  'YYYY/MM/DD':
    /^(20\d{2}|2[1-9]\d{2})\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/,
};

const validDatesAlphaNumeric: Record<string, RegExp> = {
  'MMM-YY': /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{2})$/i,
  'MMM+-YY':
    /^(January|February|March|April|May|June|July|August|September|October|November|December)-(\d{2})$/i,
  'MMM YY': /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{2})$/i,
  'MMM+ YY':
    /^(January|February|March|April|May|June|July|August|September|October|November|December) (\d{2})$/i,
  'MMM-YYYY': /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(2\d{3})$/i,
  'MMM+-YYYY':
    /^(January|February|March|April|May|June|July|August|September|October|November|December)-(20\d{2}|2[1-9]\d{2})$/i,
  'MMM YYYY':
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (20\d{2}|2[1-9]\d{2})$/i,
  'MMM+ YYYY':
    /^(January|February|March|April|May|June|July|August|September|October|November|December) (20\d{2}|2[1-9]\d{2})$/i,
};

export const getDateFormatAlphaNumeric = (val: string, dateFormat?: string) => {
  const formats: string[] = [];

  if (dateFormat) {
    const regex = validDatesAlphaNumeric[dateFormat];
    if (regex && regex.test(val)) {
      formats.push(dateFormat);
    }
  } else {
    for (const [format, regex] of Object.entries(validDatesAlphaNumeric)) {
      if (regex.test(val)) {
        formats.push(format);
      }
    }
  }

  if (formats.length) return formats;
  throw new Error(UNSUPPORTED_DATE_ERR);
};

export const getDateFormatNumeric = (val: string, dateFormat?: string) => {
  const formats: string[] = [];

  if (dateFormat) {
    const regex = validDatesNumeric[dateFormat];
    if (regex && regex.test(val)) {
      formats.push(dateFormat);
    }
  } else {
    for (const [format, regex] of Object.entries(validDatesNumeric)) {
      if (regex.test(val)) {
        formats.push(format);
      }
    }
  }

  if (formats.length) return formats;
  throw new Error(UNSUPPORTED_DATE_ERR);
};

const isValidDateFormat = (dateStr: string, dateFormat?: string) => {
  if (/[a-zA-Z]+/.test(dateStr)) {
    return getDateFormatAlphaNumeric(dateStr, dateFormat);
  }
  return getDateFormatNumeric(dateStr, dateFormat);
};

export const getDateFormat = (datesArr: string[], dateFormat?: string) => {
  const formatSet = new Set<string>();

  for (const dateStr of datesArr) {
    let formats: string[] = [];

    if (/[a-zA-Z]+/.test(dateStr)) {
      formats = getDateFormatAlphaNumeric(dateStr, dateFormat);
    } else {
      formats = getDateFormatNumeric(dateStr, dateFormat);
    }

    for (const format of formats) {
      formatSet.add(format);
    }
  }

  if (formatSet.size === 1) {
    return formatSet.values().next().value as string;
  }

  const filteredFormats: string[] = [];

  /* Ensures only a single format is found.
    Note: "May" matches both MMM & MMM+. This edge case is taken into account
    and handled by ensuring ALL values match either MMM or MMM+ */
  for (const format of formatSet.values()) {
    let regex: RegExp | null = null;

    if (validDatesNumeric[format as keyof typeof validDatesNumeric]) {
      regex = validDatesNumeric[format as keyof typeof validDatesNumeric];
    }
    if (validDatesAlphaNumeric[format as keyof typeof validDatesAlphaNumeric]) {
      regex =
        validDatesAlphaNumeric[format as keyof typeof validDatesAlphaNumeric];
    }

    if (!regex) {
      throw new Error('Invalid date format');
    }

    const filteredDates = datesArr.filter((dateStr) => regex?.test(dateStr));
    if (filteredDates.length === datesArr.length) {
      filteredFormats.push(format);
    }
  }

  if (filteredFormats.length === 1) {
    return filteredFormats[0];
  }

  if (!filteredFormats.length) {
    throw new Error(UNSUPPORTED_DATE_ERR);
  }
  return filteredFormats;
};

const priorDateValues = new Map<string, boolean>();
const dateValidator = (val: string) => {
  try {
    if (!priorDateValues.has(val)) {
      priorDateValues.set(val, !!isValidDateFormat(val));
    }
    return !!priorDateValues.get(val);
  } catch {
    return false;
  }
};

// --- End date format validation ---

// Validator functions for the columns that should be checked (not all columns are checked)
type ValidatorFunc = (val: string) => boolean;
type DetectPercentageFormat = (val: string) => string;
export type HSAColValdators = Partial<
  Record<HSAColHeader, ValidatorFunc | DetectPercentageFormat>
>;
export const HSA_COL_VALIDATORS: HSAColValdators = {
  Month: dateValidator,
  'Customer ID': validateIDValue,
  'Campaign ID': validateIDValue,
  'Ad group ID': validateIDValue,
  'Keyword ID': validateIDValue,
  'Search Impr. share (Auction Insights)': detectPercentageFormat,
  'Search overlap rate': detectPercentageFormat,
  'Position above rate': detectPercentageFormat,
  'Top of page rate': detectPercentageFormat,
  'Abs. Top of page rate': detectPercentageFormat,
  'Search outranking share': detectPercentageFormat,
};

export type HSAErrorData = { headers: HSAColHeader[] | null } & Record<
  HSAColHeader,
  number
>;

// Tracks number of errors discovered within each column
const DEFAULT_ERROR_DATA = EXPECTED_HSA_HEADERS.reduce(
  (acc: Partial<HSAErrorData>, header) => {
    // Set number of errors for each column to default of zero.
    acc[header] = 0;
    return acc;
  },
  { headers: null }
) as HSAErrorData;

// Finds weather the passed row is the header row or not
export const isColHeadersRow = (
  row: string,
  expectedHeaders = EXPECTED_HSA_HEADERS
) => {
  if (!row.length) {
    throw new Error('Row contains no value');
  }
  let numFound = 0;
  for (const header of expectedHeaders) {
    if (row.toLowerCase().includes(header.toLowerCase())) {
      numFound++;
    }
  }
  return numFound > 1;
};

// Ensures all expected headers are present
export const getMissingHeaders = (
  row: string,
  colHeaders: HSAColHeader[] | readonly HSAColHeader[],
  optHeaders: HSAColHeader[] = []
) => {
  if (!row.length) {
    throw new Error('Row contains no value');
  }
  const optHeadersSet = new Set<HSAColHeader>(optHeaders);

  const missingHeaders: HSAColHeader[] = [];
  for (const header of colHeaders) {
    if (!row.includes(header) && !optHeadersSet.has(header)) {
      missingHeaders.push(header);
    }
  }

  return missingHeaders.length ? missingHeaders : null;
};

export const getAdditionalHeaders = (
  columnHeaders: string[],
  expectedHeaders: HSAColHeader[]
) => {
  const additionalHeaders: string[] = [];

  for (const colHeader of columnHeaders) {
    if (!expectedHeaders.includes(colHeader as HSAColHeader)) {
      additionalHeaders.push(colHeader);
    }
  }

  return additionalHeaders.length ? additionalHeaders : null;
};

export const isHeaderRowOrdered = (
  columnHeaders: string[],
  expectedHeaders: string[],
  optHeaders: string[] = []
) => {
  const filteredExpected = expectedHeaders.filter(
    (h) => !optHeaders.includes(h)
  );
  const filteredReceived = columnHeaders.filter((h) => !optHeaders.includes(h));

  return filteredExpected.every(
    (header, idx) =>
      header.toLowerCase() === filteredReceived[idx].toLowerCase()
  );
};

export const validateHeaders = (
  received: string[],
  expected: HSAColHeader[] | readonly HSAColHeader[],
  optional: HSAColHeader[] = []
) => {
  const actual = expected.filter((colHeader) => !optional.includes(colHeader));

  const isOrdered = isHeaderRowOrdered(received, actual, optional);
  const additionalHeaders = getAdditionalHeaders(received, [...expected]);
  const missingHeaders = getMissingHeaders(
    received.join(','),
    EXPECTED_HSA_HEADERS,
    optional
  );

  return {
    isOrdered,
    missingHeaders,
    additionalHeaders,
  };
};

// Finds the delimiter used within file
export const findColDelimiter = (headerRow: string) => {
  if (!headerRow.length) {
    throw new Error('Row contains no value');
  }

  for (const header of EXPECTED_HSA_HEADERS) {
    if (headerRow.includes(header)) {
      const delimiter = headerRow.split(header)[1][0];
      return delimiter;
    }
  }
  throw new Error('Unable to determine delimiter');
};

export type PreviewErrorData = {
  rowNum: number;
  errCols: number[];
  value: string;
};

// Validation Result (returned for both error and success cases)
export type ValidateHSAResult = {
  errorMsg: string | null;
  errorData: HSAErrorData;
  rowNum: number;
  currentRow: string;
  columnHeaders: string[];
  colHeadersRow: number;
  previewRows: string[];
  previewErrorRows: PreviewErrorData[];
  delimiter: string;
  name: string;
  dateFormat: string | string[];
  file_percentage_format: string;
};

type ValidatorOptions = {
  optionalHeaders?: HSAColHeader[];
  dateFormat?: string;
};

// HSA File(s) validtor
export const validateHSAFile = async (
  file: File | null,
  options: ValidatorOptions = {}
): Promise<ValidateHSAResult> => {
  if (!file) {
    throw new Error('File cannont be null');
  }
  const stream = file.stream();
  const reader = stream.getReader();
  const decoder = new TextDecoder();

  let isDone = false;
  let incompleteLine = '';

  let columnHeaders: HSAColHeader[] = [];
  let colHeadersRow = -1;
  let delimiter = '';
  let dateFormat: string | string[] = '';
  let rowNum = 0;

  let currentRow = '';
  const previewRows: string[] = [];
  const previewErrorRows: PreviewErrorData[] = [];

  let errorMsg: string | null = null;
  const errorData = { ...DEFAULT_ERROR_DATA };
  const errors: string[] = [];
  const percentFormat = new Set<string>();
  let validPercentageFormat: string = '';

  try {
    do {
      const { done, value } = await reader.read();
      if (done || !value) {
        isDone = true;
      }
      const chunk = decoder.decode(value, { stream: true });
      const lines = (incompleteLine + chunk).split(/\r?\n/);
      incompleteLine = lines.pop() || '';

      for (let i = 0; i < lines.length; i++) {
        if (
          currentRow &&
          previewRows.length < MAX_PREVIEW_ROWS &&
          colHeadersRow > -1 && // ensure header column has been found
          i - 1 !== colHeadersRow // don't include header column
        ) {
          previewRows.push(currentRow);
        }

        currentRow = lines[i];

        // Determine Column Headers
        if (!columnHeaders.length && isColHeadersRow(currentRow)) {
          colHeadersRow = i;
          delimiter = findColDelimiter(currentRow);
          const headerArr = currentRow.split(delimiter).map((h) => h.trim());

          // Validate column headers of the auction insights csv file
          const { additionalHeaders, missingHeaders, isOrdered } =
            validateHeaders(
              headerArr,
              EXPECTED_HSA_HEADERS,
              options.optionalHeaders
            );

          columnHeaders = [...headerArr] as HSAColHeader[];

          // Check and collect all invalid headers and error messages
          if (missingHeaders?.length) {
            errors.push(`Missing column(s): ${missingHeaders.join(', ')}`);
          }

          if (additionalHeaders?.length) {
            errors.push(
              `These column(s) are not allowed: ${additionalHeaders.join(', ')}`
            );
          }

          if (!isOrdered) {
            errors.push('Wrong order of columns');
          }

          // Throw all relevant header error messages
          if (errors.length > 0) {
            throw new Error(errors.join(' | '));
          }

          continue;
        }

        // Validate Columns with ID values
        const values = currentRow.split(delimiter).map((v) => v.trim());
        const errCols: number[] = [];
        let newColError = false;

        for (let colIndex = 0; colIndex < values.length; colIndex++) {
          const colHeader = columnHeaders[colIndex];
          const validatorFunc = HSA_COL_VALIDATORS[colHeader || ''];

          if (validatorFunc) {
            if (validatorFunc === detectPercentageFormat) {
              // Validate for percentage format case
              const format = validatorFunc(values[colIndex]);
              percentFormat.add(format);
            } else {
              // Validate for Ids (Boolean)
              if (!validatorFunc(values[colIndex])) {
                if (errorData[colHeader] === 0) {
                  newColError = true;
                }
                errorData[colHeader]++;
                errCols.push(colIndex);
              }
            }
          }
        }

        if (errCols.length && newColError) {
          previewErrorRows.push({
            rowNum,
            errCols,
            value: currentRow,
          });
        }

        rowNum++;
      }
    } while (!isDone);

    const percentFormatFound = getValidPercentageFormat(percentFormat);
    validPercentageFormat = percentFormatFound;

    if (colHeadersRow < 0) {
      throw new Error('Unable to determine column header row.');
    }
    if (previewErrorRows.length) {
      errorMsg = 'Invalid column value(s) found:';
    }

    const dateValues = Array.from(priorDateValues.keys());
    dateFormat = getDateFormat(dateValues, options.dateFormat);

    if (Array.isArray(dateFormat)) {
      throw new Error(UNSUPPORTED_DATE_ERR);
    }
  } catch (err) {
    errorMsg = 'An unknown error occurred';
    if (err instanceof Error) {
      errorMsg = err.message;
    }
  } finally {
    priorDateValues.clear();
    reader.releaseLock();

    // remove extra increment of final iteration
    rowNum = Math.max(rowNum - 1, 0);
  }

  return {
    errorMsg,
    errorData,
    rowNum,
    colHeadersRow,
    columnHeaders,
    delimiter,
    previewRows,
    previewErrorRows,
    currentRow,
    name: file.name,
    dateFormat,
    file_percentage_format: validPercentageFormat,
  };
};
